const DEFAULT_CONFIG = {
  autoplay: true,
  speed: 3000,
  pauseOnHover: true
}

const TRANSITION_DURATION = 300
const TOUCH_SWIPE_THRESHOLD = 50

const ITEM_BASE_CLASSES = ['absolute', 'inset-0', 'translate-y-full', 'z-0']
const ACTIVE_CLASSES = ['translate-y-0', 'transition-transform', 'duration-500', 'ease-in-out', 'z-2']
const TRANSITIONING_CLASSES = ['-translate-y-full', 'transition-transform', 'duration-500', 'ease-in-out', 'z-1']

function applyItemBaseClasses(el: Element) {
  el.classList.add(...ITEM_BASE_CLASSES)
}

function setActive(el: Element) {
  el.classList.remove('translate-y-full', ...TRANSITIONING_CLASSES)
  el.classList.add(...ACTIVE_CLASSES)
}

function setTransitioning(el: Element) {
  el.classList.remove(...ACTIVE_CLASSES)
  el.classList.add(...TRANSITIONING_CLASSES)
}

function setInactive(el: Element) {
  el.classList.remove(...TRANSITIONING_CLASSES)
  el.classList.add('translate-y-full')
}

function verticalLayout(_slider: Element, items: Element[]) {
  if (items.length > 0) {
    setActive(items[0])
  }
}

function verticalTransition(items: Element[], currentIndex: number, newIndex: number) {
  setTransitioning(items[currentIndex])

  setTimeout(() => {
    setInactive(items[currentIndex])
    setActive(items[newIndex])
  }, TRANSITION_DURATION)
}

function createVerticalTouchHandler(onNext: () => void, onPrev: () => void) {
  let startY = 0
  let isDragging = false
  const threshold = TOUCH_SWIPE_THRESHOLD

  return {
    start: (e: TouchEvent) => {
      startY = e.touches[0].clientY
      isDragging = true
    },
    move: (e: TouchEvent) => {
      if (!isDragging) return
      e.preventDefault()
    },
    end: (e: TouchEvent) => {
      if (!isDragging) return
      isDragging = false
      const endY = e.changedTouches[0].clientY
      const diffY = startY - endY
      if (Math.abs(diffY) > threshold) {
        if (diffY > 0) onNext()
        else onPrev()
      }
    }
  }
}

export class VerticalCarouselElement extends HTMLElement {
  private slider: Element | null = null
  private items: Element[] = []
  private config = { ...DEFAULT_CONFIG }
  private currentIndex = 0
  private autoplayTimer: ReturnType<typeof setInterval> | null = null
  private touchHandler: ReturnType<typeof createVerticalTouchHandler> | null = null

  private boundPauseAutoplay = () => this.pauseAutoplay()

  private boundResumeAutoplay = () => {
    if (this.config.autoplay && !this.autoplayTimer) this.startAutoplay()
  }

  connectedCallback() {
    this.slider = this.querySelector('[data-track]')
    if (!this.slider) return

    const childNodes = Array.from(this.slider.children)
    if (childNodes.length === 0) return

    const track = this.slider as HTMLElement
    const computedHeight = track.getBoundingClientRect().height
    if (computedHeight > 0) {
      track.style.minHeight = `${computedHeight}px`
    }

    const heights = childNodes.map((child) => (child as HTMLElement).offsetHeight)
    const maxHeight = Math.max(...heights)
    track.style.height = `${maxHeight}px`

    const wrappers = childNodes.map((child) => {
      const wrapper = document.createElement('div')
      applyItemBaseClasses(wrapper)
      wrapper.appendChild(child)
      return wrapper
    })
    this.slider.replaceChildren(...wrappers)
    this.items = wrappers

    this.config = {
      autoplay: this.dataset.autoplay === 'true',
      speed: parseInt(this.dataset.speed ?? '', 10) || DEFAULT_CONFIG.speed,
      pauseOnHover: this.dataset.pauseHover !== 'false'
    }

    verticalLayout(this.slider, this.items)
    this.setupEventListeners()
    this.setupTouchEvents()

    this.setAttribute('data-ready', '')

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (track.style.minHeight) {
          track.style.minHeight = ''
        }
      })
    })

    if (this.config.autoplay && this.items.length > 1) {
      this.startAutoplay()
    }
  }

  disconnectedCallback() {
    this.destroy()
  }

  private next() {
    if (this.items.length <= 1) return
    const nextIndex = (this.currentIndex + 1) % this.items.length
    this.updateSlide(nextIndex)
  }

  private prev() {
    if (this.items.length <= 1) return
    const prevIndex = (this.currentIndex - 1 + this.items.length) % this.items.length
    this.updateSlide(prevIndex)
  }

  private updateSlide(newIndex: number) {
    if (!this.slider) return
    verticalTransition(this.items, this.currentIndex, newIndex)
    this.currentIndex = newIndex
  }

  private startAutoplay() {
    if (this.items.length <= 1 || !this.config.autoplay) return
    this.autoplayTimer = setInterval(() => this.next(), this.config.speed)
  }

  private pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  private measureSlideHeights(): number[] {
    if (!this.slider || this.items.length === 0) return []
    const track = this.slider as HTMLElement
    const heights: number[] = []
    const origTrackHeight = track.style.height
    const origTrackOverflow = track.style.overflow
    track.style.height = 'auto'
    track.style.overflow = 'visible'
    this.items.forEach((item, index) => {
      const el = item as HTMLElement
      this.items.forEach((other, i) => {
        (other as HTMLElement).style.visibility = i === index ? 'visible' : 'hidden'
      })
      const origPosition = el.style.position
      const origHeight = el.style.height
      el.style.position = 'relative'
      el.style.height = 'auto'
      heights.push(el.offsetHeight)
      el.style.position = origPosition
      el.style.height = origHeight
    })
    this.items.forEach((el) => {
      (el as HTMLElement).style.visibility = ''
    })
    track.style.height = origTrackHeight
    track.style.overflow = origTrackOverflow
    return heights
  }

  private applyAdaptiveHeight() {
    if (!this.slider || this.items.length === 0) return
    const heights = this.measureSlideHeights()
    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0
    ;(this.slider as HTMLElement).style.height = `${maxHeight}px`
  }

  private setupEventListeners() {
    if (this.config.pauseOnHover) {
      this.addEventListener('mouseenter', this.boundPauseAutoplay)
      this.addEventListener('mouseleave', this.boundResumeAutoplay)
    }
  }

  private setupTouchEvents() {
    this.touchHandler = createVerticalTouchHandler(
      () => this.next(),
      () => this.prev()
    )
    this.addEventListener('touchstart', this.touchHandler.start)
    this.addEventListener('touchmove', this.touchHandler.move, { passive: false })
    this.addEventListener('touchend', this.touchHandler.end)
  }

  private destroy() {
    this.pauseAutoplay()
    this.removeEventListener('mouseenter', this.boundPauseAutoplay)
    this.removeEventListener('mouseleave', this.boundResumeAutoplay)
    if (this.touchHandler) {
      this.removeEventListener('touchstart', this.touchHandler.start)
      this.removeEventListener('touchmove', this.touchHandler.move)
      this.removeEventListener('touchend', this.touchHandler.end)
      this.touchHandler = null
    }
  }
}
